import { GlobalStyles, styled } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Link from '@mui/material/Link'
import Switch from '@mui/material/Switch'
import Typography from '@mui/material/Typography'
import cookies from 'js-cookie'
import React from 'react'
import { useNavigate } from 'react-router-dom-v5-compat'

import ConfirmDialog from '@/app/component/atom/delete-dialog'
import ExternalLink from '@/app/component/atom/external-link'
import { reduceIndexed } from '@/app/module/campaigns/helpers'
import { useGetPlan } from '@/app/module/plans/hooks/use-get-plan'
import type { Plan } from '@/app/module/plans/types'
import { esApi } from '@/app/store/api'
import { useAppDispatch } from '@/app/store/redux-hooks'
import { Organization, SelectOption } from '@/app/types'

import CustomPlanItem from './custom-plan-item'
import PlanItem from './plan-item'
import PlansFeatures from './plans-features'
import { PlanAction } from './subscribe-button'
import SubscribePlan, { PlanConfirmation } from './subscribe-plan-dialog'

type SubscribeItemInput = {
  billing: 'monthly' | 'yearly'
  planId: number
  label: string
}

type Props = {
  activeOrg: Organization
  cards: SelectOption<string>[]
  orgId: number
  plans: Plan[]
  showTrialMode: boolean
  token: string
  activateTrialMode: (arg: { trialMode: boolean }) => void
  subscribePlan: (args: { orgId: number; token: string; item: SubscribeItemInput }) => Promise<void | Error>
}

const Pricing: React.FC<Props> = (props) => {
  const { activeOrg, orgId, plans = [], showTrialMode = false, token, activateTrialMode, subscribePlan } = props

  const dispatch = useAppDispatch()
  const { data: currentPlan } = useGetPlan()

  const navigate = useNavigate()
  const activePlan = currentPlan?.planId
  const [chargeYearly, setChargeYearly] = React.useState<boolean>(true)
  const [trialConfirmation, setTrialConfirmation] = React.useState<boolean>(false)
  const [page, setPage] = React.useState<number>(0)
  const [planChanged, setPlanChanged] = React.useState<boolean>(false)
  const [planConfirmation, setPlanConfirmation] = React.useState<PlanConfirmation | undefined>(undefined)

  const selectedPlanIndex = reduceIndexed(
    (acc: number, plan: Plan, index: number) => {
      if (plan.id === activePlan) {
        return index
      }
      return acc
    },
    -1,
    Array.isArray(plans) ? plans : [],
  )
  // biome-ignore lint/correctness/useExhaustiveDependencies: Navigate on plan change
  React.useEffect(() => {
    if (selectedPlanIndex > 3) {
      setPage(1)
    } else {
      setPage(0)
    }
    if (planChanged) {
      setPlanChanged(false)
      navigate('/')
    }
  }, [activePlan, planChanged, selectedPlanIndex, navigate])

  const subscribePlanAction = React.useCallback(
    ({ label, planId, upgrade = false }: { label: string; planId: number; upgrade?: boolean }) => {
      subscribePlan({
        orgId,
        token,
        item: {
          billing: upgrade || chargeYearly ? 'yearly' : 'monthly',
          planId,
          label,
        },
      }).then((res) => {
        if (!(res instanceof Error)) {
          dispatch(esApi.util.invalidateTags(['OrgPlan']))
          setPlanChanged(true)
        }
      })
    },
    [chargeYearly, orgId, token, dispatch, subscribePlan],
  )

  const generateCustomTooltip = (planId: number): React.ReactNode => {
    if (activeOrg.role !== 'owner') {
      return <>Only the owner of an organization can do this.</>
    }
    if (activePlan === planId && !chargeYearly) {
      return (
        <>
          Switch the Billing Cycle toggle above to <strong>yearly</strong> to upgrade your current plan.
        </>
      )
    }
    return ''
  }

  const handlePlanSelect = React.useCallback(
    (plan: Plan, action: PlanAction) => {
      const { id: planId, label, priceMonthly, priceYearly } = plan
      switch (action) {
        case PlanAction.Subscribe:
          setPlanConfirmation({
            callback: () => subscribePlanAction({ label, planId }),
            title: 'Subscribe!',
            plan,
            cycle: chargeYearly,
            text: (
              <>
                <Typography variant="h6">
                  Press the "Confirm" button below to subscribe to the <strong>{label}</strong> plan with{' '}
                  <strong>{chargeYearly ? 'yearly' : 'monthly'}</strong> billing.
                </Typography>
                <Typography variant="h6">{confirmText(chargeYearly ? priceYearly : priceMonthly)}</Typography>
              </>
            ),
            display: true,
          })
          break
        case PlanAction.UpgradeToYearly:
          setPlanConfirmation({
            callback: () => subscribePlanAction({ label, planId, upgrade: true }),
            title: 'Upgrade Subscription',
            plan,
            cycle: true,
            text: (
              <>
                <Typography variant="h6">
                  You are now upgrading to the <strong>{label}</strong> plan with <strong>yearly</strong> billing.
                </Typography>
                <Typography variant="h6">{confirmText(priceYearly)}</Typography>
              </>
            ),
            display: true,
          })
          break
        case PlanAction.Downgrade:
          setPlanConfirmation({
            callback: () => subscribePlanAction({ label, planId }),
            title: 'Downgrade Plan',
            plan,
            cycle: chargeYearly,
            text: (
              <>
                <Typography variant="h6">
                  You are now downgrading to the <strong>{label}</strong> plan with{' '}
                  <strong>{chargeYearly ? 'yearly' : 'monthly'}</strong> billing.
                </Typography>
                <Typography variant="h6">{confirmText(chargeYearly ? priceYearly : priceMonthly)}</Typography>
              </>
            ),
            display: true,
          })
          break
        case PlanAction.Upgrade:
          setPlanConfirmation({
            callback: () => subscribePlanAction({ label, planId }),
            title: 'Upgrade Plan',
            plan,
            cycle: chargeYearly,
            display: true,
            text: (
              <>
                <Typography variant="h6">
                  You are now upgrading to the <strong>{label}</strong> plan with{' '}
                  <strong>{chargeYearly ? 'yearly' : 'monthly'}</strong> billing.
                </Typography>
                <Typography variant="h6">{confirmText(chargeYearly ? priceYearly : priceMonthly)}</Typography>
              </>
            ),
          })
          break

        default:
          break
      }
    },
    [chargeYearly, subscribePlanAction],
  )

  return (
    <div>
      <GlobalStyles styles={globalStyles} />
      {trialConfirmation && (
        <ConfirmDialog
          text={
            <>
              <ul>
                <TrialListItem>You can test engageSPARK for 14 days before choosing a plan.</TrialListItem>
                <TrialListItem>
                  During the testing period, you can interact with a maximum of 50 contacts.
                </TrialListItem>
                <TrialListItem>
                  You will need to add funds to cover usage fees for sending SMS, calls, etc.
                </TrialListItem>
              </ul>
              <span>Reach out to support for free testing credits.</span>
            </>
          }
          onClose={() => setTrialConfirmation(false)}
          onConfirm={() => {
            setTrialConfirmation(false)
            activateTrialMode({ trialMode: true })
            cookies.set('trialMode', 'true', { sameSite: 'strict' })
          }}
          isOpen={true}
          hideDeleteButton={false}
          title="14-Day Testing Period"
          deleteButtonText="Continue Testing"
          cancelButtonText="Select a Plan"
        />
      )}
      {planConfirmation?.display && (
        <SubscribePlan
          {...props}
          planConfirmation={planConfirmation}
          onClose={() => setPlanConfirmation(undefined)}
          onConfirm={() => {
            planConfirmation.callback()
            setPlanConfirmation(undefined)
          }}
        />
      )}
      <div>
        {currentPlan?.trialDaysLeft !== undefined && currentPlan.trialDaysLeft < 0 && (
          <ExpiredTrialCont>
            <TrialSubCont>
              <CenteredTypography variant="h6" sx={{ my: 2 }}>
                You do not have an active subscription. Choose one now to engage your contacts.
              </CenteredTypography>
            </TrialSubCont>
          </ExpiredTrialCont>
        )}
        <Box
          sx={{
            py: 4,
            bgcolor: 'background.paper',
            borderTop: '1px solid',
            borderBottom: '1px solid',
            borderColor: 'primary.light',
          }}
        >
          <div>
            <CenteredTypography variant="h4" color="primary">
              Choose your Plan
            </CenteredTypography>
          </div>
          <Box sx={{ mb: 4 }}>
            <CenteredTypography variant="h6" color="textSecondary">
              Select the plan that best suits your needs—reach out to us if you have questions!
            </CenteredTypography>
          </Box>
          <Box sx={{ mx: 'auto', textAlign: 'center' }} data-testid="billing-cycle">
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <div>
                <Typography variant="h6" color={chargeYearly ? 'textSecondary' : 'primary'}>
                  monthly
                </Typography>
              </div>
              <div>
                <Switch checked={chargeYearly} onChange={() => setChargeYearly(!chargeYearly)} />
              </div>
              <div>
                <Typography variant="h6" color={chargeYearly ? 'primary' : 'textSecondary'}>
                  yearly
                </Typography>
              </div>
            </Box>
          </Box>
        </Box>
        <Box sx={{ maxWidth: '870px', mb: 12, mt: 6, mx: 'auto' }}>
          <Box sx={{ textAlign: 'right' }}>
            <Typography color="textSecondary" variant="subtitle2">
              Text messages, voice minutes, and other usage fees are charged{' '}
              <UnderlinedText>
                <strong>in addition</strong>
              </UnderlinedText>{' '}
              to Subscription Plan fees. <br />
              For more information{' '}
              <ExternalLink href="https://www.engagespark.com/pricing" target="_blank">
                click here
              </ExternalLink>
            </Typography>
          </Box>

          <PlanContainer>
            {Array.isArray(plans) &&
              plans
                .slice(DEFAULT_PAGE_SIZE * page, DEFAULT_PAGE_SIZE * page + DEFAULT_PAGE_SIZE + page)
                .map((plan, index) => (
                  <PlanItem
                    chargeYearly={chargeYearly}
                    currentPlanBilling={currentPlan?.billing as 'monthly' | 'yearly' | undefined}
                    customButtonTooltip={generateCustomTooltip(plan.id)}
                    index={index}
                    isActivePlan={activePlan === plan.id}
                    key={plan.id}
                    isOwner={activeOrg.role === 'owner'}
                    lastIndex={plans.length} // plus 1 for custom plan
                    plan={plan}
                    selectedPlanIndex={selectedPlanIndex}
                    onSelect={handlePlanSelect}
                  />
                ))}
            <CustomPlanItem />
          </PlanContainer>

          <PlansFeatures />
        </Box>
        {showTrialMode && currentPlan?.trialDaysLeft !== undefined && currentPlan.trialDaysLeft > -1 && (
          <TrialCont>
            <TrialSubCont>
              <Link href="#" onClick={() => setTrialConfirmation(true)}>
                <Typography variant="h5">Continue testing for a limited time for free {'>>'}</Typography>
              </Link>
            </TrialSubCont>
          </TrialCont>
        )}
      </div>
      {plans.length > DEFAULT_PAGE_SIZE && page === 1 && (
        <PreviousPageButtonContainer>
          <NextPageButton onClick={() => setPage(0)} color="primary" variant="contained">
            Show Basic Plans
          </NextPageButton>
        </PreviousPageButtonContainer>
      )}
      {plans.length > DEFAULT_PAGE_SIZE && page !== 1 && (
        <NextPageButtonContainer>
          <NextPageButton onClick={() => setPage(1)} color="primary" variant="contained">
            Show More Plans
          </NextPageButton>
        </NextPageButtonContainer>
      )}
    </div>
  )
}

export const TrialListItem: React.FC<React.PropsWithChildren<{}>> = ({ children }) => (
  <li style={{ listStyleType: 'square', marginLeft: '18px' }}>{children}</li>
)

const DEFAULT_PAGE_SIZE = 4

const confirmText = (price: string) => (
  <>
    We will charge <strong>${price}</strong> to your organization's balance.
  </>
)

const PlanContainer = styled('div')({
  display: 'flex',
  gap: '1px',
})

const NextPageButtonContainer = styled('div')({
  position: 'absolute',
  right: -10,
  top: '70%',
  transform: 'rotate(90deg)',
  transformOrigin: '100% 0',
})

const PreviousPageButtonContainer = styled('div')({
  position: 'absolute',
  left: 25,
  top: '65%',
  transform: 'rotate(270deg)',
  transformOrigin: '0 100%',
})

const NextPageButton = styled(Button)({
  width: '200px',
})

const CenteredTypography = styled(Typography)({
  textAlign: 'center',
})

const TrialCont = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  marginTop: '10px',
})

const ExpiredTrialCont = styled(TrialCont)(({ theme }) => ({
  color: theme.palette.secondary.main,
}))

const UnderlinedText = styled('span')({
  textDecoration: 'underline',
})

const TrialSubCont = styled('div')({})

const globalStyles = {
  ul: {
    margin: 0,
    padding: 0,
    listStyle: 'none',
  },
}

export default Pricing

export type { Props as PricingProps }
